import type { Feature, PublicUser } from "@codvero/shared";
import { Link } from "@tanstack/react-router";
import { ChevronDown, ChevronLeft, ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn.ts";
import { AccountsSettings } from "./AccountsSettings.tsx";
import { AppearanceSettings } from "./AppearanceSettings.tsx";
import { ConnectionsSettings } from "./ConnectionsSettings.tsx";
import { NotificationsSettings } from "./NotificationsSettings.tsx";
import { MyMemorySettings } from "./MyMemorySettings.tsx";
import { ProfileSettings } from "./ProfileSettings.tsx";
import { SecuritySettings } from "./SecuritySettings.tsx";
import { UpdatesSettings } from "./UpdatesSettings.tsx";
import { SupportSettings } from "./SupportSettings.tsx";
import { UsageSettings } from "./UsageSettings.tsx";
import { UsersSettings } from "./UsersSettings.tsx";

const ALL = ["profile", "memory", "appearance", "notifications", "agents", "usage", "security", "users", "support", "updates", "info"] as const;
type SectionId = (typeof ALL)[number];
const COLLAPSED_KEY = "codvero.settings.collapsed";
const readCollapsed = (): SectionId[] => {
  try { const v = JSON.parse(localStorage.getItem(COLLAPSED_KEY) ?? "[]"); return Array.isArray(v) ? v : []; } catch { return []; }
};

/** Global settings as one scrolling page with anchor tabs, mirroring the original's Preferences screen. */
export function SettingsHub({ user }: { user: PublicUser }) {
  const { t } = useTranslation();
  const isAdmin = user.role === "admin";
  const has = (f: Feature) => user.features.includes(f);
  // provider accounts: for an account that adds its own or uses the shared ones; users: admins
  const sections = ALL.filter((s) => (s !== "users" || isAdmin) && (s !== "support" || user.owner) && (s !== "memory" || has("chat")) && (!(s === "agents" || s === "usage") || has("accounts") || has("sharedAccounts") || isAdmin));
  const [active, setActive] = useState<SectionId>(() => (location.hash.replace("#settings-", "") as SectionId) || "profile");
  const tabsRef = useRef<HTMLElement>(null);
  // keep the current section's tab visible in the scrollable tab bar (phones)
  useEffect(() => {
    const el = tabsRef.current?.querySelector<HTMLElement>(`[data-tab="${active}"]`);
    if (!el || !tabsRef.current) return;
    const bar = tabsRef.current.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    if (r.left < bar.left || r.right > bar.right) tabsRef.current.scrollBy({ left: r.left - bar.left - (bar.width - r.width) / 2, behavior: "smooth" });
  }, [active]);
  const rootRef = useRef<HTMLDivElement>(null);
  // folded sections, remembered on this device; the one a link points at opens
  const [collapsed, setCollapsed] = useState<SectionId[]>(() => readCollapsed().filter((s) => `#settings-${s}` !== location.hash));
  const saveCollapsed = (next: SectionId[]) => {
    setCollapsed(next);
    try { localStorage.setItem(COLLAPSED_KEY, JSON.stringify(next)); } catch { /* private mode */ }
  };
  const toggle = (id: SectionId) => saveCollapsed(collapsed.includes(id) ? collapsed.filter((s) => s !== id) : [...collapsed, id]);
  const allFolded = sections.every((s) => collapsed.includes(s));

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (vis) setActive(vis.target.id.replace("settings-", "") as SectionId);
      },
      { rootMargin: "-20% 0px -65% 0px" },
    );
    for (const id of sections) {
      const el = rootRef.current?.querySelector(`#settings-${id}`);
      if (el) obs.observe(el);
    }
    const initial = location.hash.replace("#settings-", "");
    if (initial) rootRef.current?.querySelector(`#settings-${initial}`)?.scrollIntoView({ block: "start" });
    return () => obs.disconnect();
  }, [isAdmin]);
  const go = (id: SectionId) => {
    setActive(id);
    if (collapsed.includes(id)) saveCollapsed(collapsed.filter((s) => s !== id));
    history.replaceState(null, "", `#settings-${id}`);
    rootRef.current?.querySelector(`#settings-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div ref={rootRef} className="-mx-4 -mt-3 overflow-x-clip sm:-mx-8 sm:-mt-6">
      <div className="sticky -top-3 z-10 border-b border-border bg-bg/95 backdrop-blur sm:-top-6">
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 pt-3">
          <Link to="/" className="grid size-8 place-items-center rounded-full border border-border text-fg-muted hover:text-fg" aria-label={t("common.back")}><ChevronLeft className="size-4 rtl:-scale-x-100" /></Link>
          <div className="min-w-0">
            <div className="text-[11px] text-fg-muted">{t("settings.preferences")}</div>
            <h1 className="text-base font-semibold">{t("settings.title")}</h1>
          </div>
          <button type="button" onClick={() => saveCollapsed(allFolded ? [] : [...sections])} className="ms-auto flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs text-fg-muted hover:text-fg">
            {allFolded ? <ChevronsUpDown className="size-3.5" /> : <ChevronsDownUp className="size-3.5" />} {allFolded ? t("settings.expandAll") : t("settings.collapseAll")}
          </button>
        </div>
        <nav ref={tabsRef} className="no-scrollbar mx-auto flex max-w-4xl gap-1 overflow-x-auto px-4 pt-2 pb-2">
          {sections.map((id) => (
            <button key={id} data-tab={id} type="button" onClick={() => go(id)} className={cn("tap shrink-0 whitespace-nowrap rounded-md px-3 py-1 text-xs", active === id ? "bg-surface-2 font-medium text-fg" : "text-fg-muted hover:text-fg")}>{t(`settings.sections.${id}`)}</button>
          ))}
        </nav>
      </div>
      <div className="mx-auto max-w-4xl space-y-10 px-4 py-6">
        <Section id="profile" open={!collapsed.includes("profile")} onToggle={toggle} label={t("settings.sections.profile")} desc={t("settings.desc.profile")}><ProfileSettings user={user} /></Section>
        {sections.includes("memory") && <Section id="memory" open={!collapsed.includes("memory")} onToggle={toggle} label={t("settings.sections.memory")} desc={t("settings.desc.memory")}><MyMemorySettings /></Section>}
        <Section id="appearance" open={!collapsed.includes("appearance")} onToggle={toggle} label={t("settings.sections.appearance")} desc={t("settings.desc.appearance")}><AppearanceSettings /></Section>
        <Section id="notifications" open={!collapsed.includes("notifications")} onToggle={toggle} label={t("settings.sections.notifications")} desc={t("settings.desc.notifications")}><NotificationsSettings /></Section>
        {sections.includes("agents") && <Section id="agents" open={!collapsed.includes("agents")} onToggle={toggle} label={t("settings.sections.agents")} desc={t("settings.desc.agents")}><AccountsSettings isAdmin={isAdmin} canAdd={has("accounts")} /></Section>}
        {sections.includes("usage") && <Section id="usage" open={!collapsed.includes("usage")} onToggle={toggle} label={t("settings.sections.usage")} desc={t("settings.desc.usage")}><UsageSettings isAdmin={isAdmin} /></Section>}
        <Section id="security" open={!collapsed.includes("security")} onToggle={toggle} label={t("settings.sections.security")} desc={t("settings.desc.security")}><SecuritySettings user={user} /></Section>
        {isAdmin && <Section id="users" open={!collapsed.includes("users")} onToggle={toggle} label={t("settings.sections.users")} desc={t("settings.desc.users")}><UsersSettings selfId={user.id} /></Section>}
        {user.owner && <Section id="support" open={!collapsed.includes("support")} onToggle={toggle} label={t("settings.sections.support")} desc={t("settings.desc.support")}><SupportSettings /></Section>}
        <Section id="updates" open={!collapsed.includes("updates")} onToggle={toggle} label={t("settings.sections.updates")} desc={t("settings.desc.updates")}><UpdatesSettings isAdmin={isAdmin} /></Section>
        <Section id="info" open={!collapsed.includes("info")} onToggle={toggle} label={t("settings.sections.info")} desc={t("settings.desc.info")}><ConnectionsSettings /></Section>
      </div>
    </div>
  );
}

function Section({ id, label, desc, open, onToggle, children }: { id: SectionId; label: string; desc: string; open: boolean; onToggle: (id: SectionId) => void; children: ReactNode }) {
  return (
    <section id={`settings-${id}`} className="scroll-mt-28 space-y-3">
      <button type="button" aria-expanded={open} aria-controls={`settings-${id}-body`} onClick={() => onToggle(id)} className="group flex w-full items-start gap-3 rounded-md text-start">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-fg-muted group-hover:text-fg">{label}</div>
          <p className="mt-1 text-sm text-fg-muted">{desc}</p>
        </div>
        <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full border border-border text-fg-muted group-hover:text-fg"><ChevronDown className={cn("size-4 transition-transform", !open && "-rotate-90 rtl:rotate-90")} /></span>
      </button>
      {open && <div id={`settings-${id}-body`}>{children}</div>}
    </section>
  );
}
